import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType, IdentityProviderResponse } from '@/types';
import { PublicClientApplication } from '@azure/msal-browser';
import { buildMsalConfig, loginRequest } from '@/config/authConfig';
import { fetchGraphUserData, fetchGraphUserPhoto } from '@/utils/graphService';
import { fetchIdentityProviderBySubdomain } from '@/utils/identityService';
import { getSubdomain } from '@/utils/subdomain';
import { STORAGE_KEYS } from '@/config/appConfig';
import { authenticateWithM365, authenticateWithPassword, getCurrentUser, logout as apiLogout } from '@/services/authApiService';
import { getToken, getUserFromToken, hasValidToken, purgeLegacySessionKeys, removeToken, setToken } from '@/services/authService';
import { getEmployeeById } from '@/features/employees';
import { restoreSession } from '@/shared/api/apiClient';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  // Restore the session, or initialize from the JWT token
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      // One-time cleanup for installs that predate tab-scoped sessions.
      purgeLegacySessionKeys();

      // The access token lives in memory, so a page reload always starts without one.
      // The HttpOnly refresh cookie is what survives — trade it for a fresh access token
      // before deciding the user is signed out. Skipping this would sign everyone out on
      // every refresh of the page, which is the exact problem this feature exists to fix.
      if (!hasValidToken()) {
        const restored = await restoreSession();
        if (cancelled) return;

        if (!restored) {
          // No usable refresh cookie: genuinely signed out. Drop the cached profile too,
          // or a stale `hrms-user` entry keeps the route guards satisfied with no live
          // session behind it and every screen renders before its API calls 401.
          removeToken();
          setLoading(false);
          return;
        }
      }

      await hydrateUser();
    };

    void restore();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hydrateUser = async () => {
    const savedUser = sessionStorage.getItem(STORAGE_KEYS.user);

    if (savedUser) {
      // Profile cached for this tab, use it
      try {
        setUser(JSON.parse(savedUser));
        setLoading(false);
        return;
      } catch (parseError) {
        console.error('Failed to parse saved user:', parseError);
        sessionStorage.removeItem(STORAGE_KEYS.user);
      }
    }

    // No cached profile — this is a reopened tab, since sessionStorage dies with the tab.
    // Rebuild from the token first: it carries the role, so the role-gated navigation and
    // route guards resolve immediately and stay correct even if the profile call fails.
    const userPayload = getUserFromToken();
    if (!userPayload) {
      setLoading(false);
      return;
    }

    const userFromToken: User = {
      id: userPayload.userId,
      employeeCode: userPayload.employeeCode,
      employeeId: userPayload.userId,
      name: userPayload.name,
      email: userPayload.email,
      designation: userPayload.department || 'Employee',
      department: userPayload.department || 'General',
      tenantId: userPayload.tenantId,
      joiningDate: new Date().toISOString().split('T')[0],
      status: 'active',
      role: userPayload.role,
      photoUrl: userPayload.photoUrl,
    };

    setUser(userFromToken);
    setLoading(false);

    // The token has no employee code and, on a refreshed token, no department — fill those
    // from the API. Persisting the result also restores the X-Tenant-Id header, which
    // apiClient reads out of this same storage key.
    try {
      const profile = await getCurrentUser();
      const fullUser: User = {
        ...userFromToken,
        name: profile.name || userFromToken.name,
        email: profile.email || userFromToken.email,
        employeeCode: profile.employeeId || userFromToken.employeeCode,
        designation: profile.jobTitle || userFromToken.designation,
        department: profile.department || userFromToken.department,
        tenantId: profile.tenantId || userFromToken.tenantId,
        role: profile.role || userFromToken.role,
      };
      setUser(fullUser);
      sessionStorage.setItem(STORAGE_KEYS.user, JSON.stringify(fullUser));
    } catch (profileError) {
      // Non-fatal: the token-derived user above is already usable, role included.
      console.warn('Could not load full profile after session restore:', profileError);
    }
  };

  const login = async (
    subdomain?: string,
    identityConfig?: IdentityProviderResponse
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      console.log('[M365 Login] Starting login with STORAGE_KEYS:', STORAGE_KEYS);

      const resolvedSubdomain = (subdomain || getSubdomain()).trim();

      const identity =
        identityConfig || (await fetchIdentityProviderBySubdomain(resolvedSubdomain));
      
      if (identity.provider.toUpperCase() !== 'M365') {
        throw new Error(`Unsupported identity provider: ${identity.provider}`);
      }

      const runtimeMsalInstance = new PublicClientApplication(buildMsalConfig(identity));
      await runtimeMsalInstance.initialize();

      setMsalInstance(runtimeMsalInstance);

      const loginResponse = await runtimeMsalInstance.loginPopup(loginRequest);
      const account = loginResponse.account;

      if (!account) {
        throw new Error('Microsoft account is not available after login');
      }

      const tokenResponse = await runtimeMsalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const m365User = await fetchGraphUserData(tokenResponse.accessToken);
      const photoUrl = await fetchGraphUserPhoto(tokenResponse.accessToken);
      
      // Authenticate with backend and get JWT token
      // Pass tenant ID from identity_configs to ensure correct tenant isolation
      const backendAuthResponse = await authenticateWithM365({
        azureId: m365User.id,
        displayName: m365User.displayName,
        email: m365User.mail || m365User.userPrincipalName,
        jobTitle: m365User.jobTitle,
        department: m365User.department,
        officeLocation: m365User.officeLocation,
        mobilePhone: m365User.mobilePhone,
        businessPhones: m365User.businessPhones,
        profilePhoto: m365User.photoUrl || photoUrl || undefined,
        subdomain: resolvedSubdomain,
        tenantId: identity.tenantId, // Use tenant ID from identity_configs
      });

      if (!backendAuthResponse.success) {
        throw new Error(backendAuthResponse.error || 'Backend authentication failed');
      }

      // Save token FIRST so apiClient can attach it to the getEmployeeById request.
      // Without this, getEmployeeById returns 401 and employeeDetails stays null.
      setToken(backendAuthResponse.token);

      // Fetch full employee details to get managerId
      let employeeDetails = null;
      try {
        if (backendAuthResponse.user.id && backendAuthResponse.user.tenantId) {
          employeeDetails = await getEmployeeById(
            backendAuthResponse.user.id,
            backendAuthResponse.user.tenantId
          );
        }
      } catch (error) {
        console.warn('Failed to fetch employee details:', error);
        // Continue with login even if employee details fetch fails
      }

      // Map backend user data to app user format
      const appUser: User = {
        id: backendAuthResponse.user.id,
        employeeCode: backendAuthResponse.user.employeeId,
        name: backendAuthResponse.user.name,
        email: backendAuthResponse.user.email,
        designation: backendAuthResponse.user.jobTitle,
        department: backendAuthResponse.user.department,
        avatar: backendAuthResponse.user.avatar || photoUrl || undefined,
        employeeId: backendAuthResponse.user.id,
        tenantId: backendAuthResponse.user.tenantId,
        role: backendAuthResponse.user.role,
        photoUrl: backendAuthResponse.user.photoUrl || photoUrl || undefined,
        joiningDate: new Date().toISOString().split('T')[0],
        status: 'active',
        isOnboardingComplete: backendAuthResponse.user.isOnboardingComplete ?? true,
        isProbationComplete: backendAuthResponse.user.isProbationComplete ?? false,
        isProbation: backendAuthResponse.user.isProbation ?? false,
        probationStartDate: backendAuthResponse.user.probationStartDate,
        probationEndDate: backendAuthResponse.user.probationEndDate,
        probationDurationDays: backendAuthResponse.user.probationDurationDays,
        managerId: employeeDetails?.managerId,
        manager: employeeDetails?.managerName,
        gender: employeeDetails?.genderValue,
      };

      console.log('[M365 Login] User data:', {
        isProbationComplete: appUser.isProbationComplete,
        isProbation: appUser.isProbation,
        probationStartDate: appUser.probationStartDate,
        probationEndDate: appUser.probationEndDate,
        isOnboardingComplete: appUser.isOnboardingComplete
      });

      console.log('[M365 Login] Saving session with keys:', {
        userKey: STORAGE_KEYS.user,
        tokenKey: STORAGE_KEYS.token,
        userData: appUser
      });

      setUser(appUser);

      try {
        sessionStorage.setItem(STORAGE_KEYS.user, JSON.stringify(appUser));
        // Token was already saved above before getEmployeeById(); this is a safe re-set.
        setToken(backendAuthResponse.token);
        console.log('[M365 Login] session write completed successfully');
      } catch (storageError) {
        console.error('[M365 Login] Failed to save session:', storageError);
      }
      
      console.log('[M365 Login] After save - session check:', {
        savedUser: sessionStorage.getItem(STORAGE_KEYS.user),
        savedToken: getToken() ? 'Token exists' : 'No token',
        allSessionStorageKeys: Object.keys(sessionStorage)
      });
    } catch (err: any) {
      console.error('Login failed:', err);
      const errorMessage = err.errorMessage || err.message || 'Authentication failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loginWithPassword = async (email: string, password: string): Promise<{ requiresPasswordChange?: boolean }> => {
    try {
      setLoading(true);
      setError(null);

      // Authenticate with backend using auth service
      // No tenantId: the server resolves it from the employee record (the email is globally
      // unique). Sending the empty system tenant here used to get stamped into the JWT and
      // broke permissions for every employee on a real tenant.
      const backendAuthResponse = await authenticateWithPassword({ email, password });

      if (!backendAuthResponse.success) {
        throw new Error(backendAuthResponse.error || 'Authentication failed');
      }

      // Save token FIRST so apiClient can attach it to the getEmployeeById request.
      setToken(backendAuthResponse.token);

      // Fetch full employee details to get managerId
      let employeeDetails = null;
      try {
        if (backendAuthResponse.user.id && backendAuthResponse.user.tenantId) {
          employeeDetails = await getEmployeeById(
            backendAuthResponse.user.id,
            backendAuthResponse.user.tenantId
          );
        }
      } catch (error) {
        console.warn('Failed to fetch employee details:', error);
        // Continue with login even if employee details fetch fails
      }

      // Map backend user data to app user format
      const appUser: User = {
        id: backendAuthResponse.user.id,
        employeeCode: backendAuthResponse.user.employeeId,
        name: backendAuthResponse.user.name,
        email: backendAuthResponse.user.email,
        designation: backendAuthResponse.user.jobTitle,
        department: backendAuthResponse.user.department,
        avatar: backendAuthResponse.user.avatar,
        employeeId: backendAuthResponse.user.id,
        tenantId: backendAuthResponse.user.tenantId,
        role: backendAuthResponse.user.role,
        photoUrl: backendAuthResponse.user.photoUrl || undefined,
        joiningDate: new Date().toISOString().split('T')[0],
        status: 'active',
        isOnboardingComplete: backendAuthResponse.user.isOnboardingComplete ?? true,
        isProbationComplete: backendAuthResponse.user.isProbationComplete ?? false,
        isProbation: backendAuthResponse.user.isProbation ?? false,
        probationStartDate: backendAuthResponse.user.probationStartDate,
        probationEndDate: backendAuthResponse.user.probationEndDate,
        probationDurationDays: backendAuthResponse.user.probationDurationDays,
        managerId: employeeDetails?.managerId,
        manager: employeeDetails?.managerName,
        gender: employeeDetails?.genderValue,
      };

      console.log('[Password Login] User data:', {
        isProbationComplete: appUser.isProbationComplete,
        isProbation: appUser.isProbation,
        probationStartDate: appUser.probationStartDate,
        probationEndDate: appUser.probationEndDate,
        isOnboardingComplete: appUser.isOnboardingComplete
      });

      setUser(appUser);
      sessionStorage.setItem(STORAGE_KEYS.user, JSON.stringify(appUser));
      // Token was already saved above before getEmployeeById(); this is a safe re-set.
      setToken(backendAuthResponse.token);

      return { requiresPasswordChange: backendAuthResponse.requiresPasswordChange ?? false };
    } catch (err: any) {
      console.error('Password login failed:', err);
      const errorMessage = err?.message || 'Authentication failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loginWithDemoCredentials = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Authenticate with backend API using password login
      // Demo credentials: system@pm-people.com / Admin@123
      const backendAuthResponse = await authenticateWithPassword({
        email: 'system@pm-people.com',
        password: 'Admin@123',
        tenantId: '00000000-0000-0000-0000-000000000000', // Demo tenant ID
      });

      if (!backendAuthResponse.success) {
        throw new Error(backendAuthResponse.error || 'Demo authentication failed');
      }

      // Save token FIRST so apiClient can attach it to the getEmployeeById request.
      setToken(backendAuthResponse.token);

      // Fetch full employee details to get managerId and other data
      let employeeDetails = null;
      try {
        if (backendAuthResponse.user.id && backendAuthResponse.user.tenantId) {
          employeeDetails = await getEmployeeById(
            backendAuthResponse.user.id,
            backendAuthResponse.user.tenantId
          );
        }
      } catch (error) {
        console.warn('Failed to fetch employee details:', error);
        // Continue with login even if employee details fetch fails
      }

      // Map backend user data to app user format
      // IMPORTANT: employeeId must be the UUID (user.id), NOT the employee code string (user.employeeId)
      const appUser: User = {
        id: backendAuthResponse.user.id,
        employeeCode: backendAuthResponse.user.employeeId,   // e.g. "EMP-DEMO-001"
        name: backendAuthResponse.user.name,
        email: backendAuthResponse.user.email,
        designation: backendAuthResponse.user.jobTitle,
        department: backendAuthResponse.user.department,
        role: backendAuthResponse.user.role,
        avatar: backendAuthResponse.user.avatar,
        photoUrl: backendAuthResponse.user.photoUrl,
        employeeId: backendAuthResponse.user.id,             // UUID used for API calls
        joiningDate: employeeDetails?.joiningDate || new Date().toISOString().split('T')[0],
        phone: employeeDetails?.phoneNumber || '',
        location: '',
        manager: employeeDetails?.managerName || '',
        managerId: employeeDetails?.managerId,
        status: 'active',
        tenantId: backendAuthResponse.user.tenantId,
        isOnboardingComplete: backendAuthResponse.user.isOnboardingComplete ?? false,
        isProbationComplete: backendAuthResponse.user.isProbationComplete ?? false,
        isProbation: backendAuthResponse.user.isProbation ?? false,
        probationStartDate: backendAuthResponse.user.probationStartDate,
        probationEndDate: backendAuthResponse.user.probationEndDate,
        probationDurationDays: backendAuthResponse.user.probationDurationDays,
        gender: employeeDetails?.genderValue,
      };

      console.log('[Demo Login] User data:', {
        isProbationComplete: appUser.isProbationComplete,
        isProbation: appUser.isProbation,
        probationStartDate: appUser.probationStartDate,
        probationEndDate: appUser.probationEndDate,
        isOnboardingComplete: appUser.isOnboardingComplete
      });

      setUser(appUser);
      sessionStorage.setItem(STORAGE_KEYS.user, JSON.stringify(appUser));
      // Token was already saved above before getEmployeeById(); this is a safe re-set.
      setToken(backendAuthResponse.token);

    } catch (err: any) {
      console.error('Demo login failed:', err);
      const errorMessage = err?.message || 'Demo login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setUser(null);

    // Best-effort call to revoke the refresh-token cookie server-side. Awaited
    // before clearing local state so it isn't racing an in-flight request that
    // might still need the (about-to-be-removed) session; failures are swallowed
    // inside apiLogout itself so a network error never blocks local logout.
    await apiLogout();

    // Everything tied to the signed-in person — token, profile, attendance,
    // per-session flags — lives in sessionStorage, so one clear() removes all of
    // it and nothing leaks into the next account signed in on this browser. MSAL's
    // token cache is here too (cacheLocation: 'sessionStorage').
    sessionStorage.clear();

    // localStorage deliberately survives: it now only holds device preferences
    // (theme, detected timezone, Web Push flags), which are not user data. The
    // push marker in particular must persist — the browser notification grant is
    // per-origin, so clearing it would re-prompt someone who already dismissed it.
    purgeLegacySessionKeys();

    // Sign out from M365 if session exists
    if (msalInstance) {
      msalInstance
        .logoutPopup({
          postLogoutRedirectUri: window.location.origin,
        })
        .catch((err) => {
          console.error('M365 logout failed:', err);
        });
    }
  };

  const value: AuthContextType = {
    user,
    login,
    loginWithPassword,
    loginWithDemoCredentials,
    setUser,
    logout,
    loading,
    isAuthenticated: !!user,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};